<script lang="ts">
import {defineComponent, shallowRef, type PropType} from 'vue';
import type {Container} from '../../container.ts';
import {AllSpiceJsonLoader} from '../../features/allspicejson.ts';
import {FetchRetryer} from '../../fetchretryer.ts';
import {buildPreviewRoute, buildDiffPreviewRoute} from '../AllSpiceDiff/AllSpiceDiffViewRouteBuilder.ts';
import {applyColorProfile} from '../pcb_colors.ts';
import {useDiffToggleStore} from '../../allspice_app/stores/diffToggle.ts';
import {mapStores} from 'pinia';
import {isNone, sortCaseInsensitiveBy} from '../../js_utils.ts';
import EcadInspectorFrame from '../EcadInspectorFrame.vue';
import {headOrBase, InspectorContext} from '../inspector_tree.ts';
import {LibraryComponent, LibraryComponentGroup, type ComponentLibraryFileType, type RawComponent} from './library_component.ts';
import ComponentLibraryRowGroup from './ComponentLibraryRowGroup.vue';
import {joinPagesByAlignment, type RawComponentPair} from './joinPagesByAlignment.ts';
import {parseSvgText} from '../svg_utils.ts';
import type {ProjectData} from '../../allspice_app/types/project-data.ts';
import type {DiffPreviewData} from '../../allspice_app/types/diff-preview-data.ts';

export type ComponentLibraryData = {
  isModelLoading: boolean,
  isModelLoaded: boolean,
  isHeadSvgLoading: boolean,
  isHeadSvgLoaded: boolean,
  showErrorDetails: boolean,
  errorDetailsText: string,
  fetchHeadJson: FetchRetryer,
  fetchBaseJson: FetchRetryer,
  fetchHeadSvg: FetchRetryer,
  fetchDiffFrom: FetchRetryer,
  fetchDiffTo: FetchRetryer,
  isDiffFromLoaded: boolean,
  isDiffToLoaded: boolean,
  isDiffLoadedPromise: Promise<[any, any]>,
  isDiffLoadError: boolean,
  areDocumentsLoadedPromise: Promise<void>,
  areDocumentsLoadedPromiseResolve: () => void,
  areDocumentsLoadedPromiseReject: (error: Error) => void,
  isAdded: boolean,
  isRemoved: boolean,
  id: string,
  headSvg: SVGSVGElement | null,
  diffFromSvg: SVGSVGElement | null,
  diffToSvg: SVGSVGElement | null,
  components: LibraryComponent[],
  nets: Record<string, unknown>,
  inspectorComponentId: string,
  queryDocId: string,
  projectData: Record<string, any> | null,
};

export default defineComponent({
  components: {
    ComponentLibraryRowGroup,
    EcadInspectorFrame,
  },
  inject: {
    untypedContainer: {from: 'container'},
  },

  props: {
    // True if displaying a diff between two commits, false if viewing a file.
    // When in view file mode, use the head parameters to specify commit, tree
    // path, etc.
    isDiff: {
      type: Boolean,
      required: false,
      default: true,
    },
    baseRepoOwner: {
      type: String,
      required: false,
      default: '',
    },
    baseRepoName: {
      type: String,
      required: false,
      default: '',
    },
    baseCommit: {
      type: String,
      required: false,
      default: '',
    },
    baseTreePath: {
      type: String,
      required: false,
      default: '',
    },
    headRepoOwner: {
      type: String,
      required: true,
      default: '',
    },
    headRepoName: {
      type: String,
      required: true,
      default: '',
    },
    headCommit: {
      type: String,
      required: true,
      default: '',
    },
    headTreePath: {
      type: String,
      required: true,
      default: '',
    },
    // Value is either three dots or two dots.
    diffType: {
      type: String,
      required: false,
      default: '...',
    },
    // Value is either 'schematic' or 'library'.
    fileType: {
      type: String as PropType<ComponentLibraryFileType>,
      required: true,
    },
    renderParams: {
      type: Object,
      required: false,
      default: null,
    },
    // Whether panning and zooming is enabled.
    isPanZoomEnabled: {
      type: Boolean,
      required: false,
      default: true,
    },
    colorProfile: {
      type: Object,
      required: false,
      default: null,
    },
    folded: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data(): ComponentLibraryData {
    // TODO: This component is pretty tangled. The data properties need access to a typed injected container
    // and also have properties that need to be shallowRefs (allegedly), but those refs also need access to properties
    // on the component like the container and repo/commit info in order to construct the FetchRetryers.
    // I'm force casting all these shallowRefs to the expected Data type to keep things clean throughout the component
    // and Vue handles this construction as well, this is just to keep TypeScript happy. Hopefully this can all get cleaned up
    // in a CompositionAPI update
    const container = this.untypedContainer as unknown as Container;
    const fetchHeadJson = shallowRef(FetchRetryer.for(
      container,
      AllSpiceJsonLoader.buildUrl(
        container,
        this.headRepoOwner,
        this.headRepoName,
        this.headCommit,
        this.headTreePath) ?? '',
    ));
    const hasHead = Boolean(fetchHeadJson.value.url);
    // If there's no URL, resolve immediately.
    if (!hasHead) {
      fetchHeadJson.value.resolve();
    }

    const fetchBaseJson = shallowRef(FetchRetryer.for(
      container,
      AllSpiceJsonLoader.buildUrl(
        container,
        this.baseRepoOwner,
        this.baseRepoName,
        this.baseCommit,
        this.baseTreePath) ?? '',
    ));
    const hasBase = Boolean(fetchBaseJson.value.url);
    // If there's no URL, resolve immediately.
    if (!hasBase) {
      fetchBaseJson.value.resolve();
    }

    let areDocumentsLoadedPromiseResolve: () => void;
    let areDocumentsLoadedPromiseReject: (error: Error) => void;
    const areDocumentsLoadedPromise = new Promise<void>((resolve, reject) => {
      areDocumentsLoadedPromiseResolve = resolve;
      areDocumentsLoadedPromiseReject = reject;
    });

    const fetchHeadSvg = shallowRef(FetchRetryer.for(
      container,
      this.headPreviewUrl(),
    ));

    const fetchDiffFrom = shallowRef(FetchRetryer.for(
      container,
      this.diffFromPreviewUrl()));
    fetchDiffFrom.value.promise
     // eslint-disable-next-line github/no-then
      .then(this.onDiffFromLoad.bind(this), this.onDiffError.bind(this));

    const fetchDiffTo = shallowRef(FetchRetryer.for(
      container,
      this.diffToPreviewUrl()));
    fetchDiffTo.value.promise
      // eslint-disable-next-line github/no-then
      .then(this.onDiffToLoad.bind(this), this.onDiffError.bind(this));

    const isDiffLoadedPromise = Promise.all([
      fetchDiffFrom.value.promise,
      fetchDiffTo.value.promise,
    ]);

    const queryString = window.location.search;
    const queryParameters = new URLSearchParams(queryString);

    let queryDocId = '';
    if (queryParameters.has('view-coordinates')) {
      queryDocId = queryParameters.get('doc-id');
    }

    return {
      isModelLoading: true,
      isModelLoaded: false,
      isHeadSvgLoading: true,
      isHeadSvgLoaded: false,
      showErrorDetails: false,
      errorDetailsText: '',
      // See note at top of data() for context on this mess
      fetchHeadJson: fetchHeadJson as unknown as FetchRetryer,
      fetchBaseJson: fetchBaseJson as unknown as FetchRetryer,
      fetchHeadSvg: fetchHeadSvg as unknown as FetchRetryer,
      fetchDiffFrom: fetchDiffFrom as unknown as FetchRetryer,
      fetchDiffTo: fetchDiffTo as unknown as FetchRetryer,
      isDiffFromLoaded: false,
      isDiffToLoaded: false,
      isDiffLoadedPromise,
      isDiffLoadError: false,
      areDocumentsLoadedPromise,
      areDocumentsLoadedPromiseResolve,
      areDocumentsLoadedPromiseReject,
      isAdded: this.isDiff && hasHead && !hasBase,
      isRemoved: this.isDiff && !hasHead && hasBase,
      id: '',
      headSvg: null,
      diffFromSvg: null,
      diffToSvg: null,
      components: [],
      nets: {},
      inspectorComponentId: '',
      queryDocId,
      projectData: null,
    };
  },

  computed: {
    ...mapStores(useDiffToggleStore),
    container(): Container {
      return this.untypedContainer as unknown as Container;
    },
    isLoading() {
      return this.isModelLoading || this.isHeadSvgLoading;
    },

    headerClassNames() {
      if (this.isAdded) {
        return 'added';
      }
      if (this.isRemoved) {
        return 'removed';
      }
      return '';
    },

    isDiffLoading() {
      return this.isDiff && (!this.isDiffFromLoaded || !this.isDiffToLoaded) &&
        !this.isDiffLoadError;
    },

    isLibrary() {
      return this.fileType === 'library';
    },

    isSchematic() {
      return this.fileType === 'schematic';
    },

    extendedRenderParams() {
      return {
        areDocumentsLoadedPromise: this.areDocumentsLoadedPromise,
        isDiffLoadedPromise: this.isDiffLoadedPromise,
        projectData: this.projectData,
        ...this.renderParams,
      };
    },
  },

  unmounted() {
    document.body.removeEventListener('keydown', this.onKeyDown);
  },

  created() {
    // Generate a unique ID.
    this.id = Math.random().toString(16).slice(2);

    // Fetch the JSON.
    this.fetchHeadJson.startFetching();
    this.fetchBaseJson.startFetching();

    // Load project data for variant support
    const projectDataPromise = this.loadProjectData();

    // Load crysknife's per-page diff alignment so we can pair head/base
    // pages even when their per-version ids differ (e.g. renamed files).
    const diffPageMappingPromise = this.loadDiffPageMapping();

    // Parse the JSON.
    const headJsonPromise = this.fetchHeadJson.promise
      // eslint-disable-next-line github/no-then
      .then(parseJsonFromResponse);
    const baseJsonPromise = this.fetchBaseJson.promise
      // eslint-disable-next-line github/no-then
      .then(parseJsonFromResponse);

    Promise.all([headJsonPromise, baseJsonPromise, projectDataPromise, diffPageMappingPromise])
      // eslint-disable-next-line github/no-then
      .then(([headJson, baseJson, _projectData, baseIdByHeadId]) => {
        // For multi-page schematics, dispatch an event with ALL pages'
        // component data so DocumentSearch can populate its source
        // immediately (without waiting for individual page accordions to
        // expand).  This event bubbles up to the .diff-file-box where
        // DocumentSearch is listening.
        // TODO: migrate to a shared Pinia store instead of bubbling DOM events.
        // DiffFilter, PcbLayerToggle, and DocumentSearch all use this pattern.
        if ((headJson?.pages || baseJson?.pages) && this.$el) {
          // Defer to the next macrotask so sibling components (e.g. DocumentSearch)
          // have time to mount and attach their event listeners.
          const el = this.$el;
          const detail = {
            schComponents: {},
            allPagesHead: headJson?.pages ? headJson : null,
            allPagesBase: baseJson?.pages ? baseJson : null,
          };
          setTimeout(() => {
            el.dispatchEvent(new CustomEvent('inspectordataload', {
              bubbles: true,
              detail,
            }));
          }, 0);
        }

        const isSchematic = this.isSchematic;

        const rawHeadDocuments: Array<RawComponent> = (isSchematic ?
          headJson?.pages as Array<RawComponent> : headJson?.components as Array<RawComponent>) ?? [];
        const rawBaseDocuments: Array<RawComponent> = (isSchematic ?
          baseJson?.pages as Array<RawComponent> : baseJson?.components as Array<RawComponent>) ?? [];

        for (const rawComponent of rawHeadDocuments) {
          if (!rawComponent.id) {
            throw new Error(`Expected component to have id: ${JSON.stringify(rawComponent)}`);
          }
        }
        for (const rawComponent of rawBaseDocuments) {
          if (!rawComponent.id) {
            throw new Error(`Expected component to have id: ${JSON.stringify(rawComponent)}`);
          }
        }

        let pairs: RawComponentPair[];
        if (this.isDiff) {
          pairs = joinPagesByAlignment(rawHeadDocuments, rawBaseDocuments, baseIdByHeadId);
        } else {
          pairs = rawHeadDocuments.map((rawHead): RawComponentPair => ({rawHead, rawBase: undefined}));
        }

        // Sort library components by name in display order.
        if (this.isLibrary) {
          pairs = sortCaseInsensitiveBy(pairs, ({rawHead, rawBase}) => (rawHead ?? rawBase)?.name ?? '');
        }

        // Convert components to our class.
        this.components = pairs.map(({rawHead, rawBase}) => {
          if (isNone(rawHead) && isNone(rawBase)) {
            throw new Error('Either base or head should be present');
          }
          const isAdded = !isNone(rawHead) && isNone(rawBase);
          const isRemoved = isNone(rawHead) && !isNone(rawBase);

          return new LibraryComponent(
            this.isDiff,
            rawHead, rawBase,
            isAdded, isRemoved,
            this.headSvg, this.diffToSvg, this.diffFromSvg,
          );
        });

        // populate a combined list of nets
        // start with the nets in the head (added, modified, unmodified)
        const combinedNets: Record<string, unknown> = headJson?.nets as Record<string, unknown> ?? {};
        // add any base-only (deleted) nets
        for (const [netId, netInfo] of Object.entries(baseJson?.nets ?? {})) {
          if (!combinedNets[netId]) {
            combinedNets[netId] = netInfo;
          }
        }
        this.nets = combinedNets;

        this.isModelLoaded = true;
        this.areDocumentsLoadedPromiseResolve();
        this.checkIfAllLoaded();
      })
      // eslint-disable-next-line github/no-then
      .catch((err) => {
        this.areDocumentsLoadedPromiseReject(err);
        this.displayUnknownError(err);
      })
      .finally(() => {
        this.isModelLoading = false;
      });

    // Fetch the SVG.
    this.fetchHeadSvg.startFetching();

    if (this.isDiff) {
      // Fetch the diff SVGs.
      this.fetchDiffFrom.startFetching();
      this.fetchDiffTo.startFetching();

      if (!this.fetchDiffFrom.url) {
        this.onDiffFromLoad();
      }
      if (!this.fetchDiffTo.url) {
        this.onDiffToLoad();
      }
    }

    /** @type {FetchRetryer} */
    // eslint-disable-next-line github/no-then
    this.fetchHeadSvg.promise.then(async (fetchHeadSvg: FetchRetryer) => {
      if (!fetchHeadSvg.response) {
        throw new Error('Response should be present after FetchRetryer resolves');
      }
      // Need to clone since this is a shared FetchRetryer, and calling `text()`
      // will consume it.
      const svgText = await fetchHeadSvg.response.clone().text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgText, 'text/xml');
      /** @type {SVGSVGElement | undefined} */
      const sharedSvgEl = doc.querySelectorAll('svg')[0];
      if (sharedSvgEl) {
        // The result may be the same shared element or a new element.
        const svgElement = this.transformHeadSvg(sharedSvgEl);
        this.headSvg = svgElement;
        this.onHeadSvgLoad();
      }
    })
      // eslint-disable-next-line github/no-then
      .catch((err: Error) => {
        this.displayUnknownError(err);
      });
  },

  methods: {
    /**
     * Fetch crysknife's diff_page.json and return a head-id -> base-id
     * mapping for renamed pages.  Crysknife emits a `basePageId` distinct
     * from `pageId` whenever the page's per-version id changed across the
     * diff (e.g. file rename or UUID change).  Returns an empty map
     * for non-schematic, view-only, or single-sided diffs, and falls back
     * to an empty map on fetch errors so a failed alignment never breaks
     * the existing union-by-id behaviour.
     */
    async loadDiffPageMapping(): Promise<Map<string, string>> {
      const mapping = new Map<string, string>();
      if (!this.isDiff || !this.isSchematic) return mapping;
      if (!this.baseTreePath || !this.headTreePath) return mapping;

      const url = buildDiffPreviewRoute(
        this.container,
        this.headRepoOwner, this.headRepoName,
        'diff_page',
        this.headCommit, this.headTreePath,
        this.baseRepoOwner, this.baseRepoName,
        this.baseCommit, this.baseTreePath,
      );

      try {
        const retryer = FetchRetryer.for(this.container, url);
        retryer.startFetching();
        await retryer.promise;
        if (!retryer.response) return mapping;
        const data: unknown = await retryer.response.clone().json();
        const pages: DiffPreviewData = Array.isArray(data) ? data as DiffPreviewData : [];
        for (const page of pages) {
          if (page.basePageId && page.basePageId !== page.pageId) {
            mapping.set(page.pageId, page.basePageId);
          }
        }
        return mapping;
      } catch (error) {
        console.warn('Failed to load diff_page mapping:', error);
        return mapping;
      }
    },

    /**
     * Load project data for variant mappings
     * @returns {Promise<void>}
     */
    async loadProjectData() {
      try {
        const prjUrl = this.isDiff ?
          AllSpiceJsonLoader.buildPrjRangeUrl(this.container, this.headRepoOwner, this.headRepoName, this.baseCommit, this.headCommit) :
          AllSpiceJsonLoader.buildPrjUrl(this.container, this.headRepoOwner, this.headRepoName, this.headCommit, this.headTreePath);

        if (prjUrl) {
          const results = await AllSpiceJsonLoader.load<ProjectData>(this.container, prjUrl, null);
          this.projectData = results.headJson;
        }
      } catch (error) {
        console.warn('Failed to load project data:', error);
      }
    },

    headPreviewUrl() {
      // This function is called from data() where the typed, computed container is not yet available,
      // Need to use the untyped injected container and cast it to Container here.
      return buildPreviewRoute(
        this.untypedContainer as unknown as Container,
        this.headRepoOwner,
        this.headRepoName,
        this.headCommit,
        this.headTreePath);
    },

    diffFromPreviewUrl() {
      if (!this.isDiff) return '';
      if (!this.baseTreePath) return '';
      // This function is called from data() where the typed, computed container is not yet available,
      // Need to use the untyped injected container and cast it to Container here.
      return buildDiffPreviewRoute(
        this.untypedContainer as unknown as Container,
        this.headRepoOwner,
        this.headRepoName,
        'from',
        this.headCommit,
        this.headTreePath,
        this.baseRepoOwner,
        this.baseRepoName,
        this.baseCommit,
        this.baseTreePath);
    },

    diffToPreviewUrl() {
      if (!this.isDiff) return '';
      if (!this.headTreePath) return '';
      // This function is called from data() where the typed, computed container is not yet available,
      // Need to use the untyped injected container and cast it to Container here.
      return buildDiffPreviewRoute(
        this.untypedContainer as unknown as Container,
        this.headRepoOwner,
        this.headRepoName,
        'to',
        this.headCommit,
        this.headTreePath,
        this.baseRepoOwner,
        this.baseRepoName,
        this.baseCommit,
        this.baseTreePath);
    },

    /**
     * Incrementally parse parts of the svg
     * @param {Array<LibraryComponent>} remainingComponents
     * @returns {(function(): void)}
     */
    incrementalSvgSplit(remainingComponents: LibraryComponent[]): () => void {
      return () => {
        if (remainingComponents.length > 0) {
          // extract the svg for this component
          remainingComponents[0].updateOutputSvg(this.headSvg, this.diffFromSvg, this.diffToSvg);

          requestAnimationFrame(this.incrementalSvgSplit(remainingComponents.slice(1)));
        } else {
          // complete the setup process
          this.onAllLoad();
        }
      };
    },

    /**
     * Returns true if this component should be hidden by default in the diff
     * @param {number} idx
     * @returns {boolean}
     */
    shouldShowComponent(idx: number) {
      return !this.isDiff ||
        (this.components[idx - 1]?.hasChanged() === true ||
          this.components[idx]?.hasChanged() === true ||
          this.components[idx + 1]?.hasChanged() === true);
    },

    /**
     * Split components into hidden/shown groups
     * @returns {Array<LibraryComponentGroup>}
     */
    componentGroups() {
      if (!this.isDiff) {
        return [new LibraryComponentGroup(this.components, true)];
      }

      let currentGroupShown = this.shouldShowComponent(0);
      const componentGroups = [new LibraryComponentGroup([], currentGroupShown)];

      for (let i = 0; i < this.components.length; i++) {
        const showComponent = this.shouldShowComponent(i);
        if (showComponent === currentGroupShown) {
          componentGroups[componentGroups.length - 1].addComponent(this.components[i]);
        } else {
          currentGroupShown = showComponent;
          componentGroups.push(new LibraryComponentGroup([this.components[i]], showComponent));
        }
      }

      return componentGroups;
    },

    /**
     * @param {Response} resp
     */
    displayDiffResponseError(resp: Response) {
      console.error(`Diff resp not found. Status code: ${resp.status}`);
      this.errorDetailsText = `Error: Failed to receive diff from server. Status code: ${resp.status}. Please contact maintainers@allspice.io if this issue persists.`;
      this.showErrorDetails = true;
    },

    displayUnknownError(_err?: Error) {
      // TODO: Use or log err, this was being passed in but not actually used
      this.errorDetailsText = 'Error: Failed to load.  Please contact maintainers@allspice.io if this issue persists.';
      this.showErrorDetails = true;
    },

    /**
     * Called when the head file has been fetched, but before it's inlined into
     * the DOM.
     * @param {SVGSVGElement} sharedSvg
     * @returns {SVGSVGElement} This needs to return either the input element or
     * a new element of the same type.
     */
    transformHeadSvg(sharedSvg: SVGSVGElement | null): SVGSVGElement | null {
      if (sharedSvg === null) {
        console.error("Couldn't find working SVG top-level element");
        return sharedSvg;
      }

      this.isHeadSvgLoading = false; // Disable loader

      return sharedSvg;
    },

    onHeadSvgLoad() {
      this.isHeadSvgLoaded = true;
      this.checkIfAllLoaded();
    },

    onDiffFromLoad() {
      this.isDiffFromLoaded = true;
      this.checkIfAllLoaded();
    },

    onDiffToLoad() {
      this.isDiffToLoaded = true;
      this.checkIfAllLoaded();
    },

    onDiffError(fetchRetryer: FetchRetryer) {
      this.isDiffLoadError = true;
      if (fetchRetryer.response) {
        this.displayDiffResponseError(fetchRetryer.response);
      } else {
        this.displayUnknownError();
      }
    },

    checkIfAllLoaded() {
      if (!this.isModelLoaded) return;
      if (!this.isHeadSvgLoaded) return;
      // If diffs are still loading, we need to wait.
      if (this.isDiffLoading) return;

      // concatenate svgs, update components
      const promise = this.diffSvgLoad();
      // eslint-disable-next-line github/no-then
      promise.then(() => {
        // concat and update component svgs
        requestAnimationFrame(this.incrementalSvgSplit(this.components));
      });
    },

    /**
     * This should only ever get called once when all SVGs are loaded, concatenated
     * and components are updated
     */
    onAllLoad() {
      if (this.colorProfile !== null) {
        const svg = this.$el.querySelector('svg.schematic_svg');
        applyColorProfile(svg, this.colorProfile);
      }
      // Once layers are loaded, enable the diff toggle buttons.
      this.enableDiffToggle();

      const metadataCtx = new InspectorContext({
        window,
        treePath: this.headTreePath,
        repoOwner: this.headRepoOwner,
        repoName: this.headRepoName,
        commitSHA: this.headCommit,
        layers: {},
        externalDesignBlocks: this.projectData?.external_design_blocks ?? null,
        projectDocuments: this.projectData?.documents ?? null,
        projectPath: this.projectData?.project_path || null,
        variants: this.projectData?.variants ?? {},
      });

      document.body.addEventListener('keydown', this.onKeyDown);

      // create event listener
      (this.$refs.componentLibrary as HTMLElement).addEventListener('library-inspector-click', (e) => {
        e.preventDefault();
        const detail = (e as CustomEvent<{
          componentId: string,
          componentDataHead: unknown[] | Record<string, unknown>,
          componentDataBase: unknown[] | Record<string, unknown>,
        }>).detail;

        // hide the inspector popup before loading/reloading
        const inspectorFrame = this.$refs.ecadInspectorFrame as typeof EcadInspectorFrame;

        inspectorFrame.showInspector(false);

        // Remember which component we're showing.
        this.inspectorComponentId = detail.componentId;

        inspectorFrame.setInspectorData(detail,
          this.$refs.componentLibrary,
          detail.componentDataHead,
          detail.componentDataBase,
          'Component',
          'Default',
          headOrBase(detail.componentDataHead, detail.componentDataBase, 'name', ''),
          '',
          metadataCtx,
        );
      });
    },

    /**
     * Event handler for keypress
     * @param {KeyboardEvent} event
     */
    onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        // hide the inspector popup
        // TODO: Once EcadInspectorFrame is refactored to be a component instead of options API change this to a cast
        const inspectorFrame = this.$refs.ecadInspectorFrame as typeof EcadInspectorFrame;
        inspectorFrame.showInspector(false);
      }
    },

    diffSvgLoad(): Promise<void> | null {
      if (!this.isDiff) {
        // If we're not showing a diff, we don't need the svgs
        return Promise.resolve();
      }

      // Need to clone since this is a shared FetchRetryer, and calling `text()`
      // consumes it.
      /** @type {Promise<string>} */
      const from = this.fetchDiffFrom.response?.clone().text() ?? Promise.resolve('');
      /** @type {Promise<string>} */
      const to = this.fetchDiffTo.response?.clone().text() ?? Promise.resolve('');
      // eslint-disable-next-line github/no-then
      return Promise.all([from, to]).then(([fromText, toText]) => {
        // Parse the SVG source.
        this.diffFromSvg = parseSvgText(fromText);
        this.diffToSvg = parseSvgText(toText);
      // eslint-disable-next-line github/no-then
      }).catch(() => {
        this.isDiffLoadError = true;
        this.displayUnknownError();
      });
    },

    /**
     * Enable the diff toggle buttons.
     */
    enableDiffToggle() {
      if (this.isDiff) {
        this.diffToggleStore.enable();
      }
    },

    onCollapseComponent(componentId: string) {
      // If the inspector is being displayed for a component and that component
      // is collapsed, hide the inspector.
      if (this.inspectorComponentId === componentId) {
        const inspectorFrame = this.$refs.ecadInspectorFrame as typeof EcadInspectorFrame;
        inspectorFrame.showInspector(false);
      }
    },
  },
});

async function parseJsonFromResponse(fetchRetryer: FetchRetryer): Promise<Record<string, unknown> | null> {
  const json: Record<string, unknown> = await fetchRetryer.response?.clone()?.json();
  if (json) {
    return AllSpiceJsonLoader.standardizeJson(json);
  }
  return null;
}
</script>
<template>
  <div
    ref="componentLibrary"
    class="component-library"
  >
    <div
      v-if="isLoading"
      class="loading-container"
    >
      <div class="is-loading" />
      LOADING design file
    </div>
    <ecad-inspector-frame
      ref="ecadInspectorFrame"
      :is-diff="isDiff"
    />
    <div v-if="isModelLoaded">
      <div v-if="components && components.length > 0">
        <div class="components-table">
          <div v-if="isLibrary">
            <div
              class="table-header"
              :class="headerClassNames"
            >
              <!-- This should match .accordion-toggle-button -->
              <div style="flex: 0 0 30px" />
              <!-- This should match .component-thumbnail -->
              <div style="flex: 0 0 125px" />
              <div class="table-heading">
                Name
              </div>
              <div class="table-heading">
                Comment
              </div>
              <div class="table-heading">
                Description
              </div>
              <!-- This should match .component-row-diff-icon -->
              <div
                v-if="isDiff"
                style="flex: 0 0 30px;"
              />
            </div>
          </div>
          <component-library-row-group
            v-for="(group, idx) in componentGroups()"
            :ref="`component-group-${idx}`"
            :key="idx"
            :component-group="group"
            :nets="nets"
            :header-class-names="headerClassNames"
            :is-diff="isDiff"
            :file-type="fileType"
            :render-params="extendedRenderParams"
            :query-doc-id="queryDocId"
            :initial-expand="components.length === 1"
            :folded="folded"
            @collapse-component="onCollapseComponent"
          />
        </div>
      </div>
      <div v-else>
        <p v-if="isLibrary">
          No components.
        </p>
        <p v-if="isSchematic">
          No pages.
        </p>
      </div>
    </div>

    <div
      v-if="showErrorDetails"
      class="ui negative message"
    >
      {{ errorDetailsText }}
    </div>
  </div>
</template>
<style scoped>
.loading-container {
  width: 100%;
  text-align: center;
}

.loading-container .is-loading {
  height: 80px;
}

.components-table {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.table-header {
  display: flex;
  flex-direction: row;
}

.table-header.added {
  background: var(--color-diff-added-row-bg);
}

.table-header.removed {
  background: var(--color-diff-removed-row-bg);
}

.table-heading {
  padding: 5px 15px 5px 25px;
  align-self: center;
  text-align: left;
  flex-basis: 150px;
  flex-grow: 1;
}

</style>
